import { useState, useEffect } from "react";
import { Loader2, RefreshCw, Save } from "lucide-react";
import { colors, fonts, CONTACT_TYPES } from "../../styles/theme";
import { Button } from "../../components/ui";
import {
  fetchSystemSettings,
  updateSystemSettings,
} from "../../services/api";

export default function ReReviewCyclesSection() {
  const [cycles, setCycles] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSystemSettings()
      .then((s) => setCycles(s?.reReviewCycles || {}))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (typeId, value) => {
    const days = value === "" ? 0 : parseInt(value) || 0;
    setCycles((prev) => ({ ...prev, [typeId]: days }));
    setSuccess(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const cleaned = {};
      for (const [k, v] of Object.entries(cycles)) {
        if (v > 0) cleaned[k] = v;
      }
      await updateSystemSettings({ reReviewCycles: cleaned });
      setCycles(cleaned);
      setSuccess("Re-review cycles saved");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const typeEntries = Object.entries(CONTACT_TYPES);

  return (
    <div
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        padding: 24,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 8,
        }}
      >
        <RefreshCw size={18} color={colors.accent} />
        <h3
          style={{
            fontFamily: fonts.heading,
            fontSize: 16,
            color: colors.text,
            margin: 0,
          }}
        >
          Re-Review Cycles
        </h3>
      </div>

      <p
        style={{
          fontSize: 12,
          color: colors.textSecondary,
          marginBottom: 16,
          lineHeight: 1.5,
        }}
      >
        Set how often reviewed contacts of each type come back for re-review (in
        days). Leave blank or 0 to disable re-review for a type.
      </p>

      {error && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 12,
            borderRadius: 6,
            background: "rgba(239,68,68,0.1)",
            color: colors.danger,
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}
      {success && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 12,
            borderRadius: 6,
            background: "rgba(16,185,129,0.1)",
            color: colors.success,
            fontSize: 12,
          }}
        >
          {success}
        </div>
      )}

      {loading ? (
        <Loader2
          size={16}
          color={colors.textSecondary}
          className="spin"
        />
      ) : (
        <>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              marginBottom: 16,
            }}
          >
            {typeEntries.map(([typeId, info]) => (
              <div
                key={typeId}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "6px 10px",
                  borderRadius: 6,
                  background: colors.bg,
                  border: `1px solid ${colors.borderLight}`,
                }}
              >
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    background: info.color,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: 13,
                    fontFamily: fonts.body,
                    color: colors.text,
                    flex: 1,
                    minWidth: 120,
                  }}
                >
                  {info.label}
                </span>
                <input
                  type="number"
                  min="0"
                  value={cycles[typeId] || ""}
                  onChange={(e) => handleChange(typeId, e.target.value)}
                  placeholder="Never"
                  style={{
                    width: 80,
                    padding: "5px 8px",
                    fontSize: 12,
                    fontFamily: fonts.mono,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 4,
                    background: colors.bgCard,
                    color: colors.text,
                    textAlign: "right",
                  }}
                />
                <span
                  style={{ fontSize: 11, color: colors.textSecondary, width: 30 }}
                >
                  days
                </span>
              </div>
            ))}
          </div>
          <Button
            variant="primary"
            size="sm"
            icon={Save}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </>
      )}
    </div>
  );
}
